import { EditIcon } from '@/assets/icons/edit';
import { Accordion } from '@/components/basicComponents/accordion';
import { TableColumns } from '@/components/table/types';
import { getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import CommitmentPlanTable from '../../editCommitmentPlanPage/table/table';
import { CreateAllocationsByDateDto, ITransformedAssetClass } from '../../editCommitmentPlanPage/types';
import { transformData } from '../../editCommitmentPlanPage/utils';

interface CommitmentPlanSectionProps {
  planAllocations: CreateAllocationsByDateDto[];
  targetSubAssetClasses: boolean;
  commitmentPlanId: string | null;
}

export const CommitmentPlanSection = ({ planAllocations, targetSubAssetClasses, commitmentPlanId }: CommitmentPlanSectionProps) => {
  const navigate = useNavigate();
  const params = useParams();

  const [tableHeaderColumns, setTableHeaderColumns] = useState<TableColumns[]>([]);
  const [tableData, setTableData] = useState<ITransformedAssetClass[]>([]);
  const columnWidth = planAllocations ? 85 / planAllocations.length : 0;

  useEffect(() => {
    if (!planAllocations) return;

    const columnNames = planAllocations.map((data: CreateAllocationsByDateDto) => {
      return {
        title: getUTCDate(new Date(data.endingDate)).toUpperCase(),
        key: null as null,
        width: columnWidth,
        keySort: new Date(data.endingDate).getFullYear().toString()
      };
    });

    const transformedData = transformData(planAllocations);

    const filteredTableData = transformedData.filter((data) => data.allocations.some((allocation) => allocation.allocation !== '-'));
    setTableData(filteredTableData);

    setTableHeaderColumns([{ title: 'Asset Class', key: null, width: 15, keySort: 'deal' }, ...columnNames]);
  }, [planAllocations]);

  return (
    <Accordion id="commitmentPlan" title="Commitment Plan">
      {tableData.length ? (
        <CommitmentPlanTable
          tableData={tableData}
          tableHeaderColumns={tableHeaderColumns}
          loading={false}
          columnWidth={columnWidth}
          mode="view"
          targetSubAssetClasses={targetSubAssetClasses}
        />
      ) : (
        <CommitmentPlanNoResults>
          No commitments are scheduled. This forecast is limited to existing commitments only. Edit the commitment plan to add future investments.
          {!commitmentPlanId || (
            <EditCommitmentPlanButtonWrapper
              onClick={() => {
                navigate(
                  `/investors/${params.investorId}/${params.entityId}/editCommitmentPlan?commitmentPlanId=${commitmentPlanId}&backToTitle=All Commitment Plans`
                );
              }}
            >
              <EditIcon fill={themeVar('font.action')} />
              <AddText>Edit Commitment Plan</AddText>
            </EditCommitmentPlanButtonWrapper>
          )}
        </CommitmentPlanNoResults>
      )}
    </Accordion>
  );
};

const CommitmentPlanNoResults = styled.div`
  font-size: 18px;
  color: ${themeVar('font.base')};
`;

const EditCommitmentPlanButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const AddText = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${themeVar('font.action')};
`;
